/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';

import 'App.css';
import { es } from 'date-fns/locale/es';
import 'primeicons/primeicons.css';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import FeatherIcon from 'feather-icons-react';
import { Column } from 'primereact/column';
import { DataTable, DataTableExpandedRows } from 'primereact/datatable';

import 'primereact/resources/primereact.min.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { AnomalyRecord } from 'data/entities/AnomalyRecord';
import { DatabaseService } from './stores/DatabaseService';
import { usePowerStationFilterStore } from './stores/PowerStationFilterStore';

import { formatDate, getConditionBgClass, sortCondition, sortDate } from './Utils';
import DeleteModalMessage from './components/DeleteModalMessage';

import { useNavigate } from 'react-router-dom';
import { useToast } from 'pages/thermography/components/ToastProvider';
import { ConditionType, ConditionTypeLabel } from 'pages/thermography/model/enumerations/condition-type.model';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

registerLocale('es', es)

const AnomalyRecordList = () => {
    const { t, i18n } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: 'translation', ...options });
    const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | AnomalyRecord[]>([]);
    const [equipments, setEquipments] = useState<Record<number, string>>();

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<AnomalyRecord | null>(null);

    const activeAnomalyRecords = usePowerStationFilterStore((state) => state.activeAnomalyRecords);
    const dbService = DatabaseService.getInstance();

    const inspectionRoutes = dbService.getInspectionRecords();

    const navigate = useNavigate();
    const toast = useToast();

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!dbService.isInitialized) return;

            const equipments = await dbService.getEquipments();
            const equipmentsRecord = equipments.reduce((acc: Record<number, string>, equipment: any) => {
                acc[equipment.id] = `${equipment.code} - (${equipment.name})`;
                return acc;
            }, {} as Record<number, string>);

            if (!cancelled) setEquipments(equipmentsRecord);
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [dbService.isInitialized]);


    const renderHeader = () => {
        return (
            <div className="flex justify-content-between items-center mb-2">
            </div>
        );
    };

    const conditionBodyTemplate = (rowData: any) => {
        const condition = rowData?.condition as keyof typeof ConditionType | undefined;

        if (!condition) {
            return <div></div>;
        }

        const displayValue = ConditionTypeLabel[condition];

        if (condition === "NORMAL") {
            return <div>{displayValue}</div>;
        }

        const bgClass = getConditionBgClass(condition);
        return <div className={`cell-content ${bgClass}`}>{displayValue}</div>;
    };

    const typeBodyTemplate = (rowData: any) => {
        let text = rowData.type == "MONITORING" ? tt('pages.thermography.anomalyRecordList.yes') : ""
        return <div className={`cell-content`}>{text}</div>;
    };


    const anomalyBodyTemplate = (rowData: any) => {
        return (
            <a href={`/thermalAnomalyForm/view/${rowData.id}`} style={{ color: 'blue', textDecoration: 'underline' }}>
                {rowData.name}
            </a>);
    }


    const equipmentBodyTemplate = (rowData: any) => {
        let text = equipments && equipments[rowData.equipment.id] ? equipments[rowData.equipment.id] : "";
        return <div className={``}>{text}</div>;
    };


    const routeBodyTemplate = (rowData: any) => {
        let inspectionRoute = inspectionRoutes.find(x => x.id == rowData.route?.id);
        let text = inspectionRoute ? inspectionRoute.name : rowData.routeId;
        return <div className={``}>{text}</div>;
    };

    const createdAtBodyTemplate = (rowData: AnomalyRecord) => {
        if (!rowData.createdAt || typeof rowData.createdAt !== 'string') {
            return <div></div>;
        }
        const createdAt = dayjs(rowData.createdAt);
        return <div>{createdAt.isValid() ? formatDate(createdAt.toDate()) : ""}</div>;
    };

    const dateLimitBodyTemplate = (rowData: AnomalyRecord) => {
        let fontClass = "";
        let className = "";
        const deadlineExecutionDate = rowData.deadlineExecution ? dayjs(rowData.deadlineExecution) : null; // Ensure it's a Dayjs instance
        if (!rowData.finished && deadlineExecutionDate !== null && deadlineExecutionDate.isBefore(dayjs())) {
            fontClass = "red";
            className = "cell-content";
        }
        return (
            <div className={className} style={{ color: fontClass }}>
                {deadlineExecutionDate ? formatDate(deadlineExecutionDate.toDate()) : ""}
            </div>
        );
    };


    const finishedBodyTemplate = (rowData: AnomalyRecord) => {
        let fontClass = "";
        let className = "";
        const deadlineExecutionDate = rowData.deadlineExecution ? dayjs(rowData.deadlineExecution) : null; // Ensure it's a Dayjs instance
        const nextMonitoringDate = rowData.nextMonitoring ? dayjs(rowData.nextMonitoring) : null;
        if (
            (!rowData.finished && deadlineExecutionDate !== null && deadlineExecutionDate.isBefore(dayjs())) ||
            (!rowData.finished && nextMonitoringDate !== null && nextMonitoringDate.isBefore(dayjs()))
        ) {
            fontClass = "red";
            className = "cell-content";
        }
        return <div className={className} style={{ color: fontClass }}>{rowData.finished ? tt('pages.thermography.anomalyRecordList.yes') : tt('pages.thermography.anomalyRecordList.no')}</div>;
    }


    const nextMonitoringBodyTemplate = (rowData: AnomalyRecord) => {
        const nextMonitoringDate = rowData.nextMonitoring ? dayjs(rowData.nextMonitoring) : null;
        let fontClass = (!rowData.finished && nextMonitoringDate !== null && nextMonitoringDate.isBefore(dayjs()) ? 'red' : '');
        return (
            <div className={`cell-content`} style={{ color: fontClass }}>
                {nextMonitoringDate ? formatDate(nextMonitoringDate.toDate()) : ''}
            </div>
        );
    }


    const allowExpansion = (rowData: AnomalyRecord) => {
        // return rowData.anomalyRecordList && rowData.anomalyRecordList.length > 0;
        return false;
    };

    const editRecord = (rowData: AnomalyRecord) => {
        navigate(`thermalAnomalyForm/edit/${rowData.id}`);
    };

    const goToReport = (rowData: AnomalyRecord) => {
        navigate(`/thermalRecordForm/report/${rowData.id}`);
    };

    /***************************** Delete Table Row ************************ */
    const deleteRecord = (rowData: AnomalyRecord) => {
        setRowToDelete(rowData);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        try {
            if (rowToDelete) {
                // deleteActiveAnomalyRecord(rowToDelete.id);
                let response = await dbService.deleteAnomalyRecord(rowToDelete.id);
                if (response) {
                    setShowDeleteModal(false);
                    setRowToDelete(null);
                    toast.showToast(tt('pages.thermography.anomalyRecordList.toast.successTitle'), tt('pages.thermography.anomalyRecordList.toast.successDelete'));
                }
            }

        } catch (error) {
            console.error("Erro ao excluir registro", error);
            toast.showToast(tt('pages.thermography.anomalyRecordList.toast.errorTitle'), tt('pages.thermography.anomalyRecordList.toast.errorDelete'));
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setRowToDelete(null);
    };
    /***************************** Delete Table Row ************************ */

    const actionBodyTemplate = (rowData: AnomalyRecord) => {
        const isSelected = false;

        return (
            <div className={`flex gap-2 action-buttons ${isSelected ? "selected" : ""}`}>
                <Button
                    onClick={() => goToReport(rowData)}
                    className={`icon-button pdf-button ${isSelected ? "selected" : ""}`}
                    aria-label={tt('pages.thermography.anomalyRecordList.actions.viewReportAria')}
                    title={tt('pages.thermography.anomalyRecordList.actions.viewPdfTitle')}
                >
                    <FeatherIcon icon="file-text" size={16} />
                    {/* <FontAwesomeIcon icon={faFilePdf}/> */}
                </Button>

                <Button
                    onClick={() => editRecord(rowData)}
                    className={`icon-button edit-button ${isSelected ? "selected" : ""}`}
                    aria-label={tt('pages.thermography.anomalyRecordList.actions.editAria')}
                    title={tt('pages.thermography.anomalyRecordList.actions.editTitle')}
                >
                    <FeatherIcon icon="edit-2" size={16} />
                </Button>

                <Button
                    onClick={() => deleteRecord(rowData)}
                    className={`icon-button delete-button ${isSelected ? "selected" : ""}`}
                    aria-label={tt('pages.thermography.anomalyRecordList.actions.deleteAria')}
                    title={tt('pages.thermography.anomalyRecordList.actions.removeTitle')}
                >
                    <FeatherIcon icon="trash-2" size={16} />
                </Button>
            </div>
        );
    };



    const rowExpansionTemplate = (data: AnomalyRecord) => {
        // let items = data.anomalyRecordList.slice(0);
        let items = [];

        return (
            <div>
                <DataTable
                    value={items}
                    showHeaders={false}
                    className="custom-datatable"
                    size="small"
                >
                    <Column />


                    <Column
                        field="name"
                        header={tt('pages.thermography.anomalyRecordList.table.record')}
                        style={{ width: '10%' }}
                        body={anomalyBodyTemplate}
                    />
                    <Column field="routeId" body={routeBodyTemplate} header={tt('pages.thermography.anomalyRecordList.table.inspectionRecord')} style={{ width: '10%' }} />
                    <Column field="equipmentId" body={equipmentBodyTemplate} header={tt('pages.thermography.anomalyRecordList.table.equipment')} style={{ width: '20%' }} />
                    <Column field="createdAt" header={tt('pages.thermography.anomalyRecordList.table.inspectionDate')} sortFunction={sortDate} style={{ width: '7%' }} className="text-center" />
                    <Column field="deltaT" header={tt('pages.thermography.anomalyRecordList.table.deltaTemp')} style={{ width: '7%', textAlign: 'center' }} />
                    <Column field="condition" header={tt('pages.thermography.anomalyRecordList.table.condition')} body={conditionBodyTemplate} style={{ width: '10%' }} />
                    <Column field="deadlineExecution" header={tt('pages.thermography.anomalyRecordList.table.executionDeadline')} body={dateLimitBodyTemplate} sortFunction={sortDate} style={{ width: '7%' }} className="text-center" />
                    <Column field="type" header={tt('pages.thermography.anomalyRecordList.table.inMonitoring')} body={typeBodyTemplate} style={{ width: '7%' }} />
                    <Column field="nextMonitoring" header={tt('pages.thermography.anomalyRecordList.table.nextMonitoring')} style={{ width: '7%' }} className="text-center" />
                    <Column field="finished" header={tt('pages.thermography.anomalyRecordList.table.closed')} body={finishedBodyTemplate} style={{ width: '5%', textAlign: 'center' }} />
                    <Column body={actionBodyTemplate} header={tt('pages.thermography.anomalyRecordList.table.actions')} style={{ width: '5%' }} />
                </DataTable>
            </div>
        );
    };

    const rowClassName = (rowData: AnomalyRecord) => {
        if (Array.isArray(expandedRows)) {
            return expandedRows.includes(rowData) ? 'row-expanded' : '';
        } else {
            return expandedRows[rowData.id] ? 'row-expanded' : '';
        }
    };

    // const headerGroup = (
    //     <ColumnGroup>
    //         <Row>
    //             <Column header="" rowSpan={2} />
    //             <Column header="Número do Registro" rowSpan={2} />
    //             <Column header="Número da Rota" rowSpan={2} />
    //             <Column header="Registro da Anomalia" colSpan={4} headerStyle={{ textAlign: 'center' }} />
    //             <Column header="Monitoramento da Anomalia" colSpan={5} headerStyle={{ textAlign: 'center' }} />
    //         </Row>
    //         <Row>
    //             <Column header="Data da Inspeção" />
    //             <Column header="Prazo da Execução" />
    //             <Column header="Condição" />
    //             <Column header="Dif. de Temp (°C)" />
    //             <Column header="Data Monitoramento" />
    //             <Column header="Próximo Monitoramento" />
    //             <Column header="Condição" />
    //             <Column header="Dif. de Temp (°C)" />
    //             <Column header="Encerrado" />
    //         </Row>
    //     </ColumnGroup>
    // );

    return (
        <div className="card">
            {equipments && (
                <>
                    <DataTable
                        key={`anomaly-record-table-${i18n.language}`}
                        value={activeAnomalyRecords}
                        className="custom-datatable"
                        size="small"
                        dataKey="id"

                        sortMode="multiple"
                        removableSort

                        expandedRows={expandedRows}
                        onRowToggle={(e) => setExpandedRows(e.data)}
                        rowExpansionTemplate={rowExpansionTemplate}
                        tableStyle={{ minWidth: '50rem' }}
                        rowClassName={rowClassName}
                        emptyMessage={tt("pages.thermography.inspectionRecordsTable.emptyMessage")}
                    // selection={selectedAnomalyRecord}
                    // onSelectionChange={(e) => setSelectedAnomalyRecord(e.value)}
                    >
                        {/* <Column expander={allowExpansion} style={{ width: '1%' }} className="" /> */}

                        <Column
                            sortable
                            field="name"
                            header={tt('pages.thermography.anomalyRecordList.table.record')}
                            style={{ width: '10%' }}
                            body={anomalyBodyTemplate}
                        />
                        <Column sortable field="routeId" body={routeBodyTemplate} header={tt('pages.thermography.anomalyRecordList.table.inspectionRecord')} style={{ width: '10%' }} />
                        <Column sortable field="equipmentId" body={equipmentBodyTemplate} header={tt('pages.thermography.anomalyRecordList.table.equipment')} style={{ width: '20%' }} />
                        <Column sortable field="createdAt" header={tt('pages.thermography.anomalyRecordList.table.inspectionDate')} body={createdAtBodyTemplate} sortFunction={sortDate} style={{ width: '7%' }} className="text-center" />
                        <Column sortable field="deltaT" header={tt('pages.thermography.anomalyRecordList.table.deltaTemp')} style={{ width: '8%', textAlign: 'center' }} />
                        <Column sortable field="condition" header={tt('pages.thermography.anomalyRecordList.table.condition')} body={conditionBodyTemplate} sortFunction={sortCondition} style={{ width: '10%' }} />
                        <Column sortable field="deadlineExecution" header={tt('pages.thermography.anomalyRecordList.table.executionDeadline')} body={dateLimitBodyTemplate} sortFunction={sortDate} style={{ width: '8%' }} className="text-center" />
                        {/* <Column sortable field="type" header={tt('pages.thermography.anomalyRecordList.table.inMonitoring')} body={typeBodyTemplate} style={{ width: '8%' }} /> */}
                        <Column sortable field="nextMonitoring" body={nextMonitoringBodyTemplate} header={tt('pages.thermography.anomalyRecordList.table.nextMonitoring')} sortFunction={sortDate} style={{ width: '8%' }} className="text-center" />
                        <Column sortable field="finished" header={tt('pages.thermography.anomalyRecordList.table.closed')} body={finishedBodyTemplate} style={{ width: '5%', textAlign: 'center' }} />
                        <Column body={actionBodyTemplate} header={tt('pages.thermography.anomalyRecordList.table.actions')} style={{ width: '6%' }} />
                    </DataTable>

                    <DeleteModalMessage
                        message={rowToDelete ? tt('pages.thermography.anomalyRecordList.deleteConfirm', { name: rowToDelete.name }) : ''}
                        show={showDeleteModal}
                        onConfirm={handleConfirmDelete}
                        onCancel={handleCancelDelete}
                    />
                </>
            )}
        </div>
    );
};

export default AnomalyRecordList;
